import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, Printer, Clock, Package, User, MapPin, RefreshCw, Plus, X, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NATURE_OPTIONS = [
  { value: "Purchase-RMT/FNG", label: "Purchase - RMT/FNG" },
  { value: "Purchase-CTM", label: "Purchase - CTM" },
  { value: "Jobwork-Own", label: "Jobwork - Own" },
  { value: "Jobwork-Client", label: "Jobwork - Client" },
  { value: "Sales Return", label: "Sales Return" },
  { value: "Samples Returned", label: "Samples Returned" },
  { value: "Samples Received", label: "Samples Received" }
];

const getDeviceId = () => {
  return localStorage.getItem('security_device_id') || '';
};

export default function GateEntryRegisterPublicPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [session, setSession] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    supplier_name_raw: '',
    invoice_dc_no: '',
    nature_of_inward: '',
    vehicle_no: '',
    driver_name: '',
    driver_contact: '',
    remarks: '',
    items: []
  });

  const fetchRegister = useCallback(async () => {
    const deviceId = getDeviceId();
    if (!deviceId) {
      toast.error('No device ID found. Please check in first.');
      navigate('/security-checkin');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/store/gate-entries/public/register?device_id=${deviceId}`);
      setEntries(response.data.entries || []);
      setSession(response.data.session || null);
    } catch (error) {
      console.error('Error fetching register:', error);
      if (error.response?.status === 403) {
        toast.error('Session expired. Please check in again.');
        navigate('/security-checkin');
      } else {
        toast.error('Failed to load register');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchRegister();
  }, [fetchRegister]);

  const openEditDialog = (entry) => {
    setEditEntry(entry);
    setEditForm({
      supplier_name_raw: entry.supplier_name_raw || '',
      invoice_dc_no: entry.invoice_dc_no || '',
      nature_of_inward: entry.nature_of_inward || '',
      vehicle_no: entry.vehicle_no || '',
      driver_name: entry.driver_name || '',
      driver_contact: entry.driver_contact || '',
      remarks: entry.remarks || '',
      items: entry.items || [{ item_name: '', quantity: '', num_packages: '' }]
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editEntry) return;
    
    setSaving(true);
    try {
      const deviceId = getDeviceId();
      await axios.patch(
        `${API_URL}/store/gate-entries/public/${editEntry.id}?device_id=${deviceId}`,
        editForm
      );
      toast.success('Entry updated successfully');
      setEditDialogOpen(false);
      fetchRegister();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    
    setSaving(true);
    try {
      const deviceId = getDeviceId();
      await axios.delete(`${API_URL}/store/gate-entries/public/${entryToDelete.id}?device_id=${deviceId}`);
      toast.success('Entry deleted successfully');
      setDeleteDialogOpen(false);
      fetchRegister();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Edit form item handlers
  const addEditItem = () => {
    setEditForm(prev => ({
      ...prev,
      items: [...prev.items, { item_name: '', quantity: '', num_packages: '' }]
    }));
  };

  const removeEditItem = (index) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateEditItem = (index, field, value) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-muted-foreground">Loading register...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/gate-entry')}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-lg font-semibold">Gate Entry Register</h1>
                {session && (
                  <p className="text-sm text-muted-foreground">
                    {session.location} • {formatDate(session.checked_in_at)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchRegister}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.location.href = '/security-checkin'}
                data-testid="home-btn"
              >
                <Home className="w-4 h-4 mr-1" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Session Info */}
      {session && (
        <div className="max-w-7xl mx-auto px-4 py-4 print:py-2">
          <Card className="print:shadow-none print:border-2">
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{session.security_person_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{session.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>Check-in: {formatTime(session.checked_in_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-muted-foreground" />
                  <span>Total Entries: <strong>{session.total_entries}</strong></span>
                </div>
                <Badge variant={session.status === 'active' ? 'default' : 'secondary'}>
                  {session.status === 'active' ? 'Active Session' : 'Session Ended'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Entries Table */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <Card className="print:shadow-none">
          <CardHeader className="print:py-2">
            <CardTitle className="text-base">Entries ({entries.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No entries yet</p>
                <Button className="mt-4" onClick={() => navigate('/gate-entry')}>
                  Create First Entry
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Entry No</TableHead>
                      <TableHead className="w-[60px]">Time</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Invoice/DC</TableHead>
                      <TableHead>Nature</TableHead>
                      <TableHead className="w-[80px]">Items</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                      <TableHead className="w-[100px] print:hidden">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-mono text-xs">{entry.gate_entry_no}</TableCell>
                        <TableCell className="text-xs">{formatTime(entry.created_at)}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{entry.supplier_name_raw}</TableCell>
                        <TableCell className="text-xs">{entry.invoice_dc_no || '-'}</TableCell>
                        <TableCell className="text-xs">{entry.nature_of_inward}</TableCell>
                        <TableCell>{entry.items?.length || 0}</TableCell>
                        <TableCell>
                          <Badge variant={entry.status === 'pending' ? 'outline' : 'default'} className="text-xs">
                            {entry.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="print:hidden">
                          {entry.can_edit && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditDialog(entry)}
                                data-testid={`edit-entry-${entry.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => openDeleteDialog(entry)}
                                data-testid={`delete-entry-${entry.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Gate Entry</DialogTitle>
            <DialogDescription>
              {editEntry?.gate_entry_no}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier Name *</Label>
                <Input
                  value={editForm.supplier_name_raw}
                  onChange={(e) => setEditForm(prev => ({ ...prev, supplier_name_raw: e.target.value.toUpperCase() }))}
                />
              </div>
              <div>
                <Label>Invoice/DC No</Label>
                <Input
                  value={editForm.invoice_dc_no}
                  onChange={(e) => setEditForm(prev => ({ ...prev, invoice_dc_no: e.target.value.toUpperCase() }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nature of Inward *</Label>
                <Select
                  value={editForm.nature_of_inward}
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, nature_of_inward: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NATURE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle No</Label>
                <Input
                  value={editForm.vehicle_no}
                  onChange={(e) => setEditForm(prev => ({ ...prev, vehicle_no: e.target.value.toUpperCase() }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Driver Name</Label>
                <Input
                  value={editForm.driver_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, driver_name: e.target.value.toUpperCase() }))}
                />
              </div>
              <div>
                <Label>Driver Contact</Label>
                <Input
                  value={editForm.driver_contact}
                  onChange={(e) => setEditForm(prev => ({ ...prev, driver_contact: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Remarks</Label>
              <Textarea
                value={editForm.remarks}
                onChange={(e) => setEditForm(prev => ({ ...prev, remarks: e.target.value }))}
                rows={2}
              />
            </div>
            
            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addEditItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {editForm.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <Input
                      placeholder="Item Name"
                      value={item.item_name}
                      onChange={(e) => updateEditItem(idx, 'item_name', e.target.value.toUpperCase())}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Qty"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateEditItem(idx, 'quantity', e.target.value)}
                      className="w-24"
                    />
                    <Input
                      placeholder="Pkgs"
                      type="number"
                      value={item.num_packages}
                      onChange={(e) => updateEditItem(idx, 'num_packages', e.target.value)}
                      className="w-20"
                    />
                    {editForm.items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeEditItem(idx)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gate Entry?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete entry <strong>{entryToDelete?.gate_entry_no}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-2 { border-width: 2px !important; }
          .print\\:py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
        }
      `}</style>
    </div>
  );
}
